import { memo } from 'react'
import { AnimatedBackground } from '../animations/AnimatedBackground'
import { AnimatedSection } from '../animations/AnimatedSection'

interface HeroSectionProps {
  isAuthenticated: boolean
  onSignUpClick: () => void
  onDashboardClick: () => void
  onPricingClick: () => void
}

export const HeroSection = memo(function HeroSection({ 
  isAuthenticated, 
  onSignUpClick, 
  onDashboardClick,
  onPricingClick 
}: HeroSectionProps) {
  return (
    <section id="demo" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 pb-8" aria-labelledby="hero-heading">
      <AnimatedBackground />
      <div className="absolute inset-0 bg-gradient-radial from-accent/5 via-transparent to-transparent" aria-hidden="true"></div>
      
      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center max-w-[636px] lg:max-w-none mx-auto">
          {/* Left column */}
          <div className="text-center lg:text-left space-y-6 lg:space-y-8 mt-8 lg:mt-0 relative z-20">
            <AnimatedSection animation="fade-up">
              <div className="flex justify-center lg:justify-start">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent text-xs font-semibold uppercase tracking-wide">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse"></span>
                  Beta - Join Early
                </span>
              </div>
              <div className="space-y-3 lg:space-y-4 mt-3 lg:mt-4">
                <h1 id="hero-heading" className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight">
                  Your AI bill is{' '}
                  <span className="text-gradient">too high.</span>
                  {' '}We fix that.
                </h1>
                <p className="text-base sm:text-lg text-text-muted leading-relaxed">
                  One API. All major AI models. We handle the cost optimization behind the scenes - smart routing, caching, and prompt efficiency - so you just ship, and pay less.
                </p>
                <p className="text-sm text-text-muted/70 leading-relaxed">
                  Optimization features are rolling out progressively. Today you get unified access and predictable credit pricing. Smart routing, caching, and prompt management are coming - zero config, zero extra work on your end.
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="fade-up" delay={200}>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start relative z-20">
                {isAuthenticated ? (
                  <button
                    onClick={onDashboardClick}
                    className="btn btn-primary btn-lg w-full sm:w-auto min-h-[44px] sm:min-h-[48px] transition-all duration-200 hover:scale-[1.01] hover:shadow-lg hover:shadow-accent/20 active:scale-[0.99] transform-gpu"
                    aria-label="Navigate to your dashboard"
                  >
                    Go to Dashboard
                  </button>
                ) : (
                  <button
                    onClick={onSignUpClick}
                    className="btn btn-primary btn-lg w-full sm:w-auto min-h-[44px] sm:min-h-[48px] transition-all duration-200 hover:scale-[1.01] hover:shadow-lg hover:shadow-accent/20 active:scale-[0.99] transform-gpu"
                    aria-label="Sign up to join the beta"
                  >
                    Start Saving on AI
                  </button>
                )}
                <button
                  onClick={onPricingClick}
                  className="btn btn-secondary btn-lg w-full sm:w-auto min-h-[44px] sm:min-h-[48px]"
                  aria-label="View pricing plans"
                >
                  See Pricing
                </button>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="fade-up" delay={300}>
              <div className="flex items-center justify-center lg:justify-start gap-2 text-sm text-text-muted">
                <span className="text-lg">💸</span>
                <span className="text-xs sm:text-sm">
                  Early adopters help shape the roadmap - and lock in the best rates
                </span>
              </div>
            </AnimatedSection>
          </div>

          {/* Right column */}
          <AnimatedSection animation="fade-right" delay={300}>
            <div className="lg:order-last mt-8 lg:mt-0">
              <div className="relative">
                <div className="absolute -inset-2 lg:-inset-4 bg-gradient-to-r from-accent/20 to-accent-2/20 rounded-2xl blur-xl opacity-50 animate-pulse"></div>
                
                <div className="relative bg-panel border border-border rounded-2xl p-6 sm:p-8 shadow-2xl">
                  <div className="space-y-5">
                    {/* Value prop header */}
                    <div className="text-xs font-semibold uppercase tracking-widest text-accent/70 mb-1">What you get today</div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-accent-2">✓</span>
                        <span className="text-text-muted">Unified REST API - one key, all models</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-accent-2">✓</span>
                        <span className="text-text-muted">Predictable credit pricing, no surprise bills</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-accent-2">✓</span>
                        <span className="text-text-muted">Usage dashboard - see exactly where you spend</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-accent-2">✓</span>
                        <span className="text-text-muted">No vendor lock-in, swap models anytime</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-accent-2">✓</span>
                        <span className="text-text-muted">Smart model routing — auto-select the cheapest model that fits</span>
                      </div>
                    </div>

                    <div className="border-t border-border/50 pt-4">
                      <div className="text-xs font-semibold uppercase tracking-widest text-accent/70 mb-2">Coming soon</div>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-accent text-xs">⚡</span>
                          <span className="text-text-muted/60">Response caching <span className="text-accent text-xs">(coming soon)</span></span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-accent text-xs">⚡</span>
                          <span className="text-text-muted/60">Prompt optimization <span className="text-accent text-xs">(coming soon)</span></span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  )
})
